import React, { useState } from 'react';
import { X, ArrowRight, Brain, Code, LineChart, Database, Rocket, Palette, Server, Zap, Users, ShoppingCart } from 'lucide-react';
import ConsultationForm, { ConsultationFormData } from '../consultation/ConsultationForm';
import ConsultationSuccess from '../consultation/ConsultationSuccess';
import { submitConsultation } from '../../utils/consultation';

interface Solution {
  id: number;
  title: string;
  description: string;
  icon: typeof Brain;
  features: string[];
}

const solutions: Solution[] = [
  {
    id: 1,
    title: 'AI-Powered Websites',
    description: 'Smart websites designed to adapt to user behavior and turn visitors into customers',
    icon: Brain,
    features: [
      'Personalized user experiences',
      'Smart content adaptation',
      'Conversion optimization',
      'AI-driven analytics'
    ]
  },
  {
    id: 2,
    title: 'Web App Development',
    description: 'Powerful web applications built with the latest technology to deliver seamless performance',
    icon: Code,
    features: [
      'Custom web applications',
      'API integration',
      'Scalable architecture',
      'Performance optimization'
    ]
  },
  {
    id: 3,
    title: 'Business Intelligence',
    description: 'Turn raw data into clear insights that drive smarter decisions and business growth',
    icon: LineChart,
    features: [
      'Real-time analytics',
      'Customer insights',
      'Performance tracking',
      'ROI optimization'
    ]
  },
  {
    id: 4,
    title: 'Pino AI CRM',
    description: 'An all-in-one AI-powered system to manage, track, and grow customer relationships',
    icon: Database,
    features: [
      'Lead management & scoring',
      'Marketing automation',
      'Sales pipeline tracking',
      'AI-powered insights'
    ]
  },
  {
    id: 5,
    title: 'Digital Marketing Solutions',
    description: 'Comprehensive strategies to boost your online presence and drive growth',
    icon: Rocket,
    features: [
      'SEO optimization',
      'Content marketing',
      'Social media management',
      'PPC campaigns'
    ]
  },
  {
    id: 6,
    title: 'E-Commerce Solutions',
    description: 'End-to-end e-commerce platforms designed to drive sales and enhance user experience',
    icon: ShoppingCart,
    features: [
      'Custom e-commerce platforms',
      'Payment integration',
      'Inventory management',
      'Analytics & reporting'
    ]
  },
  {
    id: 7,
    title: 'UI/UX Design Services',
    description: 'User-centric design solutions to create intuitive and engaging digital experiences',
    icon: Palette,
    features: [
      'User research',
      'Interface design',
      'Prototyping',
      'Usability testing'
    ]
  },
  {
    id: 8,
    title: 'Managed IT Services',
    description: 'Reliable IT support to ensure your systems run smoothly and securely',
    icon: Server,
    features: [
      '24/7 system monitoring',
      'Security management',
      'Cloud solutions',
      'Technical support'
    ]
  },
  {
    id: 9,
    title: 'Automation Services',
    description: 'Streamline repetitive tasks and boost productivity with intelligent automation',
    icon: Zap,
    features: [
      'Workflow automation',
      'Process optimization',
      'Integration services',
      'Custom automation solutions'
    ]
  },
  {
    id: 10,
    title: 'Technical Consulting',
    description: 'Expert guidance to align technology with your business goals',
    icon: Users,
    features: [
      'Technology assessment',
      'Digital transformation',
      'Strategy development',
      'Implementation planning'
    ]
  }
];

interface SolutionsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SolutionsDialog({ isOpen, onClose }: SolutionsDialogProps) {
  const [selectedSolution, setSelectedSolution] = useState<Solution | null>(null);
  const [showConsultation, setShowConsultation] = useState(false);
  const [consultationSuccess, setConsultationSuccess] = useState<ConsultationFormData | null>(null);

  if (!isOpen) return null;

  const handleConsultationSubmit = async (data: ConsultationFormData) => {
    try {
      await submitConsultation(data);
      setConsultationSuccess(data);
    } catch (error) {
      console.error('Failed to submit consultation:', error);
    }
  };

  const renderContent = () => {
    if (consultationSuccess) {
      return <ConsultationSuccess consultationData={consultationSuccess} />;
    }

    if (showConsultation) {
      return (
        <div className="p-6">
          <button
            onClick={() => setShowConsultation(false)}
            className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 font-medium mb-6"
          >
            ← Back to solution details
          </button>
          <ConsultationForm
            selectedSolution={selectedSolution?.title}
            onSubmit={handleConsultationSubmit}
          />
        </div>
      );
    }

    if (selectedSolution) {
      return (
        <div className="p-6">
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg mr-4">
                <selectedSolution.icon className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                {selectedSolution.title}
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {selectedSolution.description}
            </p>
            <div className="space-y-3">
              {selectedSolution.features.map((feature, index) => (
                <div key={index} className="flex items-center text-gray-600 dark:text-gray-300">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2" />
                  {feature}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg">
            <h4 className="text-lg font-semibold text-purple-600 dark:text-purple-400 mb-2">
              Next Steps
            </h4>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Ready to get started with {selectedSolution.title}? Schedule a free consultation to discuss your project.
            </p>
            <button
              onClick={() => setShowConsultation(true)}
              className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Schedule Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
          <button
            onClick={() => setSelectedSolution(null)}
            className="mt-6 text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 font-medium"
          >
            ← Back to solutions
          </button>
        </div>
      );
    }

    return (
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {solutions.map((solution) => {
          const Icon = solution.icon;
          return (
            <button
              key={solution.id}
              onClick={() => setSelectedSolution(solution)}
              className="text-left bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center mb-4">
                <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg mr-4">
                  <Icon className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {solution.title}
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300">
                {solution.description}
              </p>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {showConsultation ? 'Schedule Your Consultation' : 
             consultationSuccess ? 'Consultation Scheduled' :
             selectedSolution ? selectedSolution.title : 'Choose Your Solution'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        {renderContent()}
      </div>
    </div>
  );
}